import React, { useState, useEffect } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../../context/AuthContext";
import { supabase } from "../../lib/supabaseClient";
import "./AdminLayout.css";

const AdminLayout = ({ children }) => {
  const [expandedMenus, setExpandedMenus] = useState({});
  const [userProfile, setUserProfile] = useState(null);
  const location = useLocation();
  const navigate = useNavigate();
  const { user, logout } = useAuth();

  useEffect(() => {
    const fetchUserProfile = async () => {
      if (!user?.email) return;
      const { data, error } = await supabase
        .from("user")
        .select("*")
        .eq("email", user.email)
        .single();

      if (data) {
        setUserProfile(data);
      }
    };
    fetchUserProfile();
  }, [user]);

  const handleLogout = async () => {
    await logout();
    navigate("/login");
  };

  const menuItems = {
    dashboard: {
      title: "대시보드",
      path: "/admin/dashboard",
      subItems: [],
    },
    members: {
      title: "회원",
      subItems: [
        { title: "회원", path: "/admin/members/users" },
        { title: "멤버쉽 회원", path: "/admin/members/membership" },
      ],
    },
    albums: {
      title: "음반",
      subItems: [
        { title: "앨범", path: "/admin/albums/list" },
        { title: "트랙", path: "/admin/albums/tracks" },
        { title: "뮤지션", path: "/admin/albums/musicians" },
        { title: "레이블", path: "/admin/albums/labels" },
        { title: "오너쉽", path: "/admin/albums/ownership" },
      ],
    },
    shorts: {
      title: "쇼츠",
      subItems: [
        { title: "회원 및 계약정보", path: "/admin/shorts/contracts" },
        { title: "음반", path: "/admin/shorts/albums" },
        { title: "채널", path: "/admin/shorts/channels" },
        { title: "[쇼츠] 허가 영상", path: "/admin/shorts/list" },
        { title: "[쇼츠] 라이센스 영상", path: "/admin/shorts/license" },
      ],
    },
    settlement: {
      title: "정산",
      subItems: [
        { title: "정산 수수료 적용", path: "/admin/settlement/commission" },
        { title: "[예정] 사용자", path: "/admin/settlement/pending-users" },
        { title: "[완료] 사용자", path: "/admin/settlement/completed-users" },
        { title: "[예정] 파트너", path: "/admin/settlement/pending-partners" },
        {
          title: "[완료] 파트너",
          path: "/admin/settlement/completed-partners",
        },
        // { title: "유튜브 업로드", path: "/admin/settlement/youtube" },
      ],
    },

    popup: {
      title: "팝업",
      subItems: [
        { title: "팝업 목록", path: "/admin/popup/list" },
        { title: "팝업 등록", path: "/admin/popup/create" },
      ],
    },
    notice: {
      title: "공지사항",
      subItems: [
        { title: "공지사항 목록", path: "/admin/notice/list" },
        { title: "공지사항 등록", path: "/admin/notice/create" },
      ],
    },
    partners: {
      title: "파트너스",
      subItems: [
        { title: "전체 정산", path: "/admin/partners/list" },
        { title: "월별 정산", path: "/admin/partners/monthly" },
      ],
    },
    support: {
      title: "고객 센터",
      subItems: [
        { title: "공지사항", path: "/admin/support/notices" },
        { title: "FAQ", path: "/admin/support/faq" },
        { title: "사용자 문의", path: "/admin/support/inquiries" },
      ],
    },
    settings: {
      title: "환경설정",
      path: "/admin/settings",
      subItems: [],
    },
  };

  const toggleMenu = (menuKey) => {
    setExpandedMenus((prev) => ({
      ...prev,
      [menuKey]: !prev[menuKey],
    }));
  };

  const getCurrentPageTitle = () => {
    const path = location.pathname;
    for (const [key, menu] of Object.entries(menuItems)) {
      if (menu.path === path) return menu.title;
      const subItem = menu.subItems.find((item) => item.path === path);
      if (subItem) return subItem.title;
    }
    return "";
  };

  return (
    <div className="admin-layout">
      <aside className="admin-sidebar" style={{ zIndex: 1000 }}>
        <div className="admin-logo">
          <Link to="/">
            <img src="/images/plpl.png" alt="로고" />
          </Link>
        </div>
        <nav className="admin-nav">
          {Object.entries(menuItems).map(([key, menu]) => (
            <div key={key} className="menu-section">
              {menu.subItems.length > 0 ? (
                <>
                  <div className="menu-title" onClick={() => toggleMenu(key)}>
                    {menu.title}
                    <span
                      className={`arrow ${
                        expandedMenus[key] ? "expanded" : ""
                      }`}
                    >
                      ▼
                    </span>
                  </div>
                  {expandedMenus[key] && (
                    <div className="submenu">
                      {menu.subItems.map((subItem, index) => (
                        <Link
                          key={index}
                          to={subItem.path}
                          className={`submenu-item ${
                            location.pathname === subItem.path ? "active" : ""
                          }`}
                        >
                          {subItem.title}
                        </Link>
                      ))}
                    </div>
                  )}
                </>
              ) : (
                <Link
                  to={menu.path}
                  className={`menu-title ${
                    location.pathname === menu.path ? "active" : ""
                  }`}
                >
                  {menu.title}
                </Link>
              )}
            </div>
          ))}
        </nav>
      </aside>
      <main className="admin-main">
        <header className="admin-header">
          <h1>{getCurrentPageTitle()}</h1>
          <div className="admin-user-info">
            <span className="user-name">
              {userProfile?.nickname || user?.email?.split("@")[0]}
            </span>
            <span className="user-email">{user?.email}</span>
            <button className="logout-button" onClick={handleLogout}>
              로그아웃
            </button>
          </div>
        </header>
        <div className="admin-content">{children}</div>
      </main>
    </div>
  );
};

export default AdminLayout;
